import axios from 'axios'
import { useAuthStore } from '../stores/authStore'
import { enqueue, requestLabel } from './offlineQueue'

const API_BASE = import.meta.env.VITE_API_BASE
  ? `${import.meta.env.VITE_API_BASE}/api`
  : '/api'

const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete'])

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config

    // Queue mutations when network is down (not a server error); never queue auth endpoints
    const isAuthEndpoint = original.url?.includes('/auth/')
    if (!error.response && MUTATION_METHODS.has(original.method?.toLowerCase()) && !original._queued && !isAuthEndpoint) {
      original._queued = true
      const token = useAuthStore.getState().accessToken
      enqueue({
        method: original.method,
        url: original.url,
        data: original.data ? JSON.parse(original.data) : undefined,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        label: requestLabel(original.method, original.url, original.data),
      })
      // Return a synthetic response so callers don't crash
      return Promise.resolve({ data: { _queued: true }, status: 202, headers: {}, config: original })
    }

    if (error.response?.status === 401 && !original._retry) {
      original._retry = true
      const refreshToken = useAuthStore.getState().refreshToken
      // null = not logged in; '' = demo session (no refresh token — don't logout)
      if (!refreshToken) return Promise.reject(error)
      try {
        const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken })
        useAuthStore.getState().setTokens(data.accessToken, refreshToken)
        original.headers.Authorization = `Bearer ${data.accessToken}`
        return api(original)
      } catch {
        useAuthStore.getState().logout()
      }
    }
    return Promise.reject(error)
  }
)

export default api
